import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { animate, svg } from 'animejs';
import { PageWrapper } from '../components/layout/PageWrapper';
import { Navbar } from '../components/layout/Navbar';
import { Badge } from '../components/shared/Badge';
import { PhaseLadder } from '../components/siege/PhaseLadder';
import { RotationRing } from '../components/siege/RotationRing';
import { GateProgress } from '../components/siege/GateProgress';
import { AccountGrid } from '../components/siege/AccountGrid';
import { MOCK_PORTFOLIO, PHASE_DEFS, getPhaseTierColor } from '../data/siege';
import { Shield } from 'lucide-react';

// ─── Chess Rook SVG ─────────────────────────────────────────────────────────

function ChessRook({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 64 80" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
      {/* Crenellations (3 merlons) */}
      <path d="M14 8 V2 H22 V8 H27 V2 H37 V8 H42 V2 H50 V8" />
      {/* Top rim */}
      <path d="M12 8 H52 V16 H12 Z" />
      {/* Neck taper */}
      <path d="M16 16 L20 30 H44 L48 16" />
      {/* Body */}
      <path d="M18 30 H46 V52 H18 Z" />
      {/* Body ridge line */}
      <path d="M18 40 H46" />
      {/* Base flare */}
      <path d="M18 52 L12 60 H52 L46 52" />
      {/* Bottom platform */}
      <path d="M8 60 H56 V68 H8 Z" />
      {/* Very bottom base */}
      <path d="M4 68 H60 V74 H4 Z" />
    </svg>
  );
}

// ─── Title Variations ───────────────────────────────────────────────────────

function TitleV1() {
  const tierColor = getPhaseTierColor(MOCK_PORTFOLIO.currentPhase);
  return (
    <div className="text-center">
      <motion.div
        className="inline-flex items-center gap-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        >
          <ChessRook className="w-8 h-10" style={{ color: tierColor, opacity: 0.7 }} />
        </motion.div>
        <div>
          <h1 className="text-[30px] font-mono font-bold tracking-[0.35em] text-white/90 leading-none">
            {'S.I.E.G.E.'.split('').map((char, i) => (
              <motion.span key={i} initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.04, type: 'spring', stiffness: 300, damping: 20 }}
                style={{ display: 'inline-block' }}>{char}</motion.span>
            ))}
          </h1>
          <motion.p className="text-[8px] font-mono tracking-[0.2em] uppercase text-white/20 mt-1"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
            Strategically Integrated Equity Growth Engine
          </motion.p>
        </div>
      </motion.div>
    </div>
  );
}

function TitleV2() {
  const tierColor = getPhaseTierColor(MOCK_PORTFOLIO.currentPhase);
  return (
    <div className="text-center">
      {/* Fortress centered above */}
      <motion.div
        className="flex justify-center mb-3"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 200, damping: 18 }}
      >
        <ChessRook className="w-10 h-12" style={{ color: tierColor, opacity: 0.5 }} />
      </motion.div>
      <h1 className="text-[32px] font-mono font-bold tracking-[0.4em] text-white/90 leading-none">
        SIEGE
      </h1>
      <motion.div className="h-px w-32 mx-auto mt-2.5 mb-2"
        style={{ background: `linear-gradient(90deg, transparent, ${tierColor}25, transparent)` }}
        initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.3, duration: 0.5 }} />
      <motion.p className="text-[8px] font-mono tracking-[0.25em] uppercase text-white/15"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
        Strategically Integrated Equity Growth Engine
      </motion.p>
    </div>
  );
}

function TitleV3() {
  const tierColor = getPhaseTierColor(MOCK_PORTFOLIO.currentPhase);
  return (
    <div className="text-center">
      <motion.div className="relative inline-block" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {/* Rook flanking left */}
        <motion.div className="absolute -left-10 top-1/2 -translate-y-1/2"
          initial={{ opacity: 0, x: -20 }} animate={{ opacity: 0.3, x: 0 }}
          transition={{ delay: 0.3, type: 'spring' }}>
          <ChessRook className="w-6 h-8" style={{ color: tierColor }} />
        </motion.div>
        {/* Rook flanking right */}
        <motion.div className="absolute -right-10 top-1/2 -translate-y-1/2"
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 0.3, x: 0 }}
          transition={{ delay: 0.3, type: 'spring' }}>
          <ChessRook className="w-6 h-8" style={{ color: tierColor, transform: 'scaleX(-1)' }} />
        </motion.div>
        <h1 className="text-[36px] font-mono font-extralight tracking-[0.5em] text-white/85 leading-none px-6">
          SIEGE
        </h1>
      </motion.div>
      <motion.p className="text-[8px] font-mono tracking-[0.3em] uppercase text-white/15 mt-2"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
        Strategically Integrated Equity Growth Engine
      </motion.p>
    </div>
  );
}

function TitleV4() {
  const tierColor = getPhaseTierColor(MOCK_PORTFOLIO.currentPhase);
  return (
    <div className="text-center">
      {/* Castle integrated into the S */}
      <motion.div className="inline-flex items-baseline gap-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <motion.div className="relative" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 250, damping: 18 }}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: `${tierColor}10`, border: `1px solid ${tierColor}20` }}>
            <ChessRook className="w-5 h-6" style={{ color: tierColor }} />
          </div>
        </motion.div>
        <h1 className="text-[34px] font-mono font-bold tracking-[0.12em] text-white/90 leading-none ml-2">
          {'IEGE'.split('').map((char, i) => (
            <motion.span key={i} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + i * 0.06 }} style={{ display: 'inline-block' }}>{char}</motion.span>
          ))}
        </h1>
      </motion.div>
      <motion.p className="text-[8px] font-mono tracking-[0.2em] uppercase text-white/18 mt-1.5"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
        Strategically Integrated Equity Growth Engine
      </motion.p>
    </div>
  );
}

function TitleV5() {
  const tierColor = getPhaseTierColor(MOCK_PORTFOLIO.currentPhase);
  return (
    <div className="text-center">
      <motion.div
        className="inline-block relative"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* SVG border box with battlements */}
        <svg className="absolute -inset-4 w-[calc(100%+32px)] h-[calc(100%+32px)]" viewBox="0 0 300 80" preserveAspectRatio="none" fill="none">
          {/* Battlement top edge */}
          <motion.path
            d="M10 15 h20 v-10 h15 v10 h30 v-10 h15 v10 h30 v-10 h15 v10 h30 v-10 h15 v10 h30 v-10 h15 v10 h30 v-10 h15 v10 h20"
            stroke={tierColor} strokeWidth="0.8" opacity="0.2"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
          />
          {/* Bottom line */}
          <motion.line x1="10" y1="70" x2="290" y2="70" stroke={tierColor} strokeWidth="0.8" opacity="0.15"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: 'easeInOut' }}
          />
        </svg>
        <h1 className="text-[38px] font-mono font-bold tracking-[0.3em] text-white/90 leading-none relative z-10">
          SIEGE
        </h1>
      </motion.div>
      <motion.p className="text-[8px] font-mono tracking-[0.25em] uppercase text-white/15 mt-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
        Strategically Integrated Equity Growth Engine
      </motion.p>
    </div>
  );
}

function TitleV6() {
  const tierColor = getPhaseTierColor(MOCK_PORTFOLIO.currentPhase);
  return (
    <div className="text-center">
      <motion.div className="flex flex-col items-center gap-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {/* Rook above with breathing glow */}
        <motion.div
          className="relative"
          animate={{
            boxShadow: [`0 0 20px ${tierColor}08`, `0 0 40px ${tierColor}18`, `0 0 20px ${tierColor}08`],
          }}
          transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
          style={{ borderRadius: '50%', padding: 8 }}
        >
          <ChessRook className="w-8 h-10" style={{ color: tierColor, opacity: 0.6 }} />
        </motion.div>
        <div>
          <h1 className="text-[26px] font-mono font-bold tracking-[0.4em] text-white/90 leading-none">
            S · I · E · G · E
          </h1>
          <motion.p className="text-[8px] font-mono tracking-[0.2em] uppercase text-white/18 mt-2"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
            Strategically Integrated Equity Growth Engine
          </motion.p>
        </div>
      </motion.div>
    </div>
  );
}

function TitleV7() {
  const tierColor = getPhaseTierColor(MOCK_PORTFOLIO.currentPhase);
  const words = ['Strategically', 'Integrated', 'Equity', 'Growth', 'Engine'];
  return (
    <div className="text-center">
      <motion.div className="flex items-center justify-center gap-3 mb-2"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <motion.div className="h-px flex-1 max-w-[60px]"
          style={{ background: `linear-gradient(90deg, transparent, ${tierColor}20)` }}
          initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.2, duration: 0.5 }} />
        <ChessRook className="w-4 h-5" style={{ color: tierColor, opacity: 0.45 }} />
        <motion.div className="h-px flex-1 max-w-[60px]"
          style={{ background: `linear-gradient(270deg, transparent, ${tierColor}20)` }}
          initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.2, duration: 0.5 }} />
      </motion.div>
      <h1 className="text-[28px] font-mono font-semibold tracking-[0.2em] text-white/90 leading-none">
        {'SIEGE'.split('').map((c, i) => (
          <motion.span key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + i * 0.06 }} style={{ display: 'inline-block' }}>{c}</motion.span>
        ))}
      </h1>
      <div className="flex items-center justify-center gap-0 mt-1">
        {words.map((w, i) => (
          <motion.span key={i} className="text-[7px] font-mono uppercase px-1"
            style={{ color: 'rgba(255,255,255,0.15)' }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 + i * 0.06 }}>
            {w}
          </motion.span>
        ))}
      </div>
    </div>
  );
}

function TitleV8() {
  const tierColor = getPhaseTierColor(MOCK_PORTFOLIO.currentPhase);
  return (
    <div className="text-center">
      <motion.div className="inline-flex flex-col items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {/* Stacked: icon box + text */}
        <motion.div
          className="w-14 h-14 rounded-xl flex items-center justify-center mb-3"
          style={{
            background: `radial-gradient(ellipse at 50% 30%, ${tierColor}08, transparent 70%)`,
            border: `1px solid ${tierColor}15`,
          }}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 16 }}
        >
          <ChessRook className="w-7 h-9" style={{ color: tierColor, opacity: 0.55 }} />
        </motion.div>
        <h1 className="text-[24px] font-mono font-bold tracking-[0.45em] text-white/85 leading-none">
          S.I.E.G.E.
        </h1>
        <motion.p className="text-[7px] font-mono tracking-[0.3em] uppercase text-white/15 mt-1.5"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          Strategically Integrated Equity Growth Engine
        </motion.p>
      </motion.div>
    </div>
  );
}

// ─── Rook path data (used for fill layers + stroke outline) ─────────────────
// Combined into one closed path for fill masking, separate paths for stroke drawing
const ROOK_FILL_PATH = "M14 8 V2 H22 V8 H27 V2 H37 V8 H42 V2 H50 V8 L52 8 V16 H48 L44 30 H46 V52 L52 60 H56 V68 H60 V74 H4 V68 H8 V60 H12 L18 52 V30 H20 L16 16 H12 V8 Z";
const ROOK_STROKE_PATHS = [
  "M14 8 V2 H22 V8 H27 V2 H37 V8 H42 V2 H50 V8",
  "M12 8 H52 V16 H12 Z",
  "M16 16 L20 30 H44 L48 16",
  "M18 30 H46 V52 H18 Z",
  "M18 40 H46",
  "M18 52 L12 60 H52 L46 52",
  "M8 60 H56 V68 H8 Z",
  "M4 68 H60 V74 H4 Z",
];

// ─── Anime.js Layered Reveal Variations (V7 base) ───────────────────────────

// V9 — Layered Depth Reveal: 3 depth layers masked, cover on top, light traces outline then cover fades
function TitleV9() {
  const tierColor = getPhaseTierColor(MOCK_PORTFOLIO.currentPhase);
  const svgRef = useRef<SVGSVGElement>(null);
  const [showText, setShowText] = useState(false);
  const words = ['Strategically', 'Integrated', 'Equity', 'Growth', 'Engine'];
  const uid = useRef(`v9-${Math.random().toString(36).slice(2, 8)}`).current;

  useEffect(() => {
    if (!svgRef.current) return;
    const cutLayer = svgRef.current.querySelector(`#${uid}-cutLayer`);
    if (!cutLayer) return;

    const drawables = svg.createDrawable(cutLayer);
    animate(drawables, {
      draw: ['0 0', '0 1'],
      ease: 'inOutQuad',
      duration: 2500,
      onComplete: () => {
        // Fade the cover to reveal layered rook underneath
        const cutGroup = svgRef.current?.querySelector(`#${uid}-cut`);
        if (cutGroup) {
          animate(cutGroup, {
            opacity: [1, 0],
            duration: 800,
            ease: 'outQuad',
            onComplete: () => setShowText(true),
          });
        }
      },
    });
  }, [uid]);

  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-3 mb-2">
        <motion.div className="h-px flex-1 max-w-[60px]"
          style={{ background: `linear-gradient(90deg, transparent, ${tierColor}20)` }}
          initial={{ scaleX: 0 }} animate={{ scaleX: showText ? 1 : 0 }}
          transition={{ duration: 0.5 }} />

        <svg ref={svgRef} viewBox="0 0 64 80" className="w-7 h-9" preserveAspectRatio="xMidYMid meet">
          <defs>
            {/* Paper texture */}
            <filter id={`${uid}-paper`}>
              <feTurbulence type="fractalNoise" baseFrequency="0.5" numOctaves="4" result="noise" />
              <feDiffuseLighting in="noise" lightingColor={tierColor} surfaceScale="1.5" result="lit">
                <feDistantLight azimuth="45" elevation="55" />
              </feDiffuseLighting>
              <feBlend in="SourceGraphic" in2="lit" mode="screen" />
            </filter>
            {/* Depth gradients */}
            <radialGradient id={`${uid}-grad3`} cx="50%" cy="40%" r="70%">
              <stop offset="0%" stopColor={tierColor} stopOpacity="0.15" />
              <stop offset="100%" stopColor={tierColor} stopOpacity="0.4" />
            </radialGradient>
            <radialGradient id={`${uid}-grad2`} cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor={tierColor} stopOpacity="0.08" />
              <stop offset="100%" stopColor={tierColor} stopOpacity="0.25" />
            </radialGradient>
            <radialGradient id={`${uid}-grad1`} cx="50%" cy="40%" r="50%">
              <stop offset="0%" stopColor={tierColor} stopOpacity="0.03" />
              <stop offset="100%" stopColor={tierColor} stopOpacity="0.12" />
            </radialGradient>
            {/* Masks for each depth layer */}
            <mask id={`${uid}-mask3`}><path d={ROOK_FILL_PATH} fill="white" transform="scale(0.92) translate(2.5,3)" /></mask>
            <mask id={`${uid}-mask2`}><path d={ROOK_FILL_PATH} fill="white" transform="scale(0.96) translate(1.2,1.5)" /></mask>
            <mask id={`${uid}-mask1`}><path d={ROOK_FILL_PATH} fill="white" /></mask>
          </defs>

          {/* Depth layers — revealed when cover fades */}
          <g id={`${uid}-layers`}>
            <rect width="64" height="80" mask={`url(#${uid}-mask3)`} fill={`url(#${uid}-grad3)`} filter={`url(#${uid}-paper)`} opacity="0.9" />
            <rect width="64" height="80" mask={`url(#${uid}-mask2)`} fill={`url(#${uid}-grad2)`} filter={`url(#${uid}-paper)`} opacity="0.9" />
            <rect width="64" height="80" mask={`url(#${uid}-mask1)`} fill={`url(#${uid}-grad1)`} filter={`url(#${uid}-paper)`} opacity="0.95" />
          </g>

          {/* Cover group — hides art, light traces through it */}
          <g id={`${uid}-cut`}>
            {/* Solid cover matching dark background */}
            <rect id={`${uid}-cutBase`} width="64" height="80" fill="#0d1117" opacity="0.97" />
            {/* Dashed outline hint */}
            <path d={ROOK_FILL_PATH} fill="none" stroke={tierColor} strokeWidth="0.3" strokeDasharray="4,3" opacity="0.08" />
            {/* The drawable — anime.js traces this */}
            <path id={`${uid}-cutLayer`} d={ROOK_FILL_PATH} fill="none" stroke={tierColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </g>
        </svg>

        <motion.div className="h-px flex-1 max-w-[60px]"
          style={{ background: `linear-gradient(270deg, transparent, ${tierColor}20)` }}
          initial={{ scaleX: 0 }} animate={{ scaleX: showText ? 1 : 0 }}
          transition={{ duration: 0.5 }} />
      </div>

      <h1 className="text-[28px] font-mono font-semibold tracking-[0.2em] text-white/90 leading-none">
        {'SIEGE'.split('').map((c, i) => (
          <motion.span key={i} initial={{ opacity: 0, y: 8 }}
            animate={showText ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: i * 0.06 }}
            style={{ display: 'inline-block' }}>{c}</motion.span>
        ))}
      </h1>
      <div className="flex items-center justify-center gap-0 mt-1">
        {words.map((w, i) => (
          <motion.span key={i} className="text-[7px] font-mono uppercase px-1"
            style={{ color: 'rgba(255,255,255,0.15)' }}
            initial={{ opacity: 0 }} animate={showText ? { opacity: 1 } : {}}
            transition={{ delay: 0.2 + i * 0.06 }}>
            {w}
          </motion.span>
        ))}
      </div>
    </div>
  );
}

// V10 — Outlined rook behind S.I.E.G.E. acronym, centered, gradient trace pulse every 6s
function TitleV10() {
  const tierColor = getPhaseTierColor(MOCK_PORTFOLIO.currentPhase);
  const svgRef = useRef<SVGSVGElement>(null);
  const [phase, setPhase] = useState<'tracing' | 'revealed' | 'done'>('tracing');
  const words = ['Strategically', 'Integrated', 'Equity', 'Growth', 'Engine'];
  const uid = useRef(`v10-${Math.random().toString(36).slice(2, 8)}`).current;
  const pulseGradId = `${uid}-pulse`;

  useEffect(() => {
    if (!svgRef.current) return;
    const cutPaths = svgRef.current.querySelectorAll(`#${uid}-cutLayer path`);
    if (cutPaths.length === 0) return;

    const drawables = svg.createDrawable(cutPaths);
    animate(drawables, {
      draw: ['0 0', '0 1'],
      ease: 'inOutQuad',
      duration: 2200,
      delay: (_el: any, i: number) => i * 100,
      onComplete: () => {
        const cutGroup = svgRef.current?.querySelector(`#${uid}-cut`);
        if (cutGroup) {
          animate(cutGroup, {
            opacity: [1, 0],
            duration: 700,
            ease: 'outQuad',
            onComplete: () => {
              setPhase('revealed');
              setTimeout(() => setPhase('done'), 100);
            },
          });
        }
      },
    });
  }, [uid]);

  return (
    <div className="text-center">
      {/* Centered composition: rook behind, title on top */}
      <div className="relative flex items-center justify-center" style={{ minHeight: 120 }}>
        {/* Large outlined rook SVG — absolutely centered behind the title */}
        <svg
          ref={svgRef}
          viewBox="0 0 64 80"
          className="absolute"
          width="90"
          height="112"
          preserveAspectRatio="xMidYMid meet"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <defs>
            <radialGradient id={`${uid}-glow`} cx="50%" cy="45%" r="60%">
              <stop offset="0%" stopColor={tierColor} stopOpacity="0.06" />
              <stop offset="100%" stopColor={tierColor} stopOpacity="0" />
            </radialGradient>

            {/* Gradient tracing pulse — sweeps across the rook every 6s */}
            <motion.linearGradient
              id={pulseGradId}
              gradientUnits="userSpaceOnUse"
              animate={{
                x1: [-20, 84],
                x2: [-10, 94],
                y1: [-10, 90],
                y2: [0, 100],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatDelay: 4,
                ease: 'linear',
              }}
            >
              <stop stopColor={tierColor} stopOpacity="0" />
              <stop stopColor={tierColor} stopOpacity="0.7" />
              <stop offset="1" stopColor={tierColor} stopOpacity="0" />
            </motion.linearGradient>
          </defs>

          {/* Subtle background glow */}
          <rect width="64" height="80" fill={`url(#${uid}-glow)`} />

          {/* Base rook — faint outlined strokes */}
          <g stroke={tierColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.15">
            {ROOK_STROKE_PATHS.map((d, i) => <path key={i} d={d} />)}
          </g>

          {/* Gradient trace overlay — same paths, stroked with the animated gradient */}
          {phase === 'done' && (
            <g strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"
              stroke={`url(#${pulseGradId})`}>
              {ROOK_STROKE_PATHS.map((d, i) => <path key={i} d={d} />)}
            </g>
          )}

          {/* Cover group — dark cover with light trace on top (initial animation) */}
          <g id={`${uid}-cut`}>
            <rect width="64" height="80" fill="#0d1117" opacity="0.98" />
            <g fill="none" stroke={tierColor} strokeWidth="0.3" strokeDasharray="4,3" opacity="0.05">
              {ROOK_STROKE_PATHS.map((d, i) => <path key={i} d={d} />)}
            </g>
            <g id={`${uid}-cutLayer`} stroke={tierColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
              {ROOK_STROKE_PATHS.map((d, i) => <path key={i} d={d} />)}
            </g>
          </g>
        </svg>

        {/* Title layer — on top of the rook */}
        <div className="relative z-10 flex flex-col items-center">
          {/* Rule lines */}
          <div className="flex items-center justify-center gap-3 mb-2">
            <motion.div className="h-px w-[50px]"
              style={{ background: `linear-gradient(90deg, transparent, ${tierColor}20)` }}
              initial={{ scaleX: 0 }} animate={{ scaleX: phase === 'done' ? 1 : 0 }}
              transition={{ duration: 0.5 }} />
            <motion.div className="h-px w-[50px]"
              style={{ background: `linear-gradient(270deg, transparent, ${tierColor}20)` }}
              initial={{ scaleX: 0 }} animate={{ scaleX: phase === 'done' ? 1 : 0 }}
              transition={{ duration: 0.5 }} />
          </div>

          {/* S.I.E.G.E. acronym */}
          <h1 className="text-[28px] font-mono font-bold tracking-[0.35em] text-white/90 leading-none">
            {'S.I.E.G.E'.split('').map((char, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: -12 }}
                animate={phase === 'done' ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.04, type: 'spring', stiffness: 300, damping: 20 }}
                style={{ display: 'inline-block' }}
              >
                {char}
              </motion.span>
            ))}
            <motion.span
              className="text-white/90"
              initial={{ opacity: 0 }}
              animate={phase === 'done' ? { opacity: 1 } : {}}
              transition={{ delay: 0.5 }}
            >
              .
            </motion.span>
          </h1>

          {/* Spelled out words */}
          <div className="flex items-center justify-center gap-0 mt-1.5">
            {words.map((w, i) => (
              <motion.span key={i} className="text-[7px] font-mono uppercase px-1"
                style={{ color: 'rgba(255,255,255,0.15)' }}
                initial={{ opacity: 0 }} animate={phase === 'done' ? { opacity: 1 } : {}}
                transition={{ delay: 0.3 + i * 0.06 }}>
                {w}
              </motion.span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// V11 — Full-canvas reveal: rook + SIEGE text all in one SVG, traced then revealed
function TitleV11() {
  const tierColor = getPhaseTierColor(MOCK_PORTFOLIO.currentPhase);
  const svgRef = useRef<SVGSVGElement>(null);
  const [showWords, setShowWords] = useState(false);
  const words = ['Strategically', 'Integrated', 'Equity', 'Growth', 'Engine'];
  const uid = useRef(`v11-${Math.random().toString(36).slice(2, 8)}`).current;

  useEffect(() => {
    if (!svgRef.current) return;

    // First trace the rook outline
    const rookDrawable = svgRef.current.querySelectorAll(`#${uid}-rookTrace path`);
    const drawables = svg.createDrawable(rookDrawable);
    animate(drawables, {
      draw: ['0 0', '0 1'],
      ease: 'inOutQuad',
      duration: 2000,
      delay: (_el: any, i: number) => i * 100,
    });

    // Trace rule lines
    const ruleDrawable = svg.createDrawable(svgRef.current.querySelectorAll(`#${uid}-rules line`));
    animate(ruleDrawable, {
      draw: ['0 0', '0 1'],
      ease: 'inOutQuad',
      duration: 700,
      delay: 1600,
    });

    // Trace SIEGE letters
    const letterDrawable = svg.createDrawable(svgRef.current.querySelectorAll(`#${uid}-letters path`));
    animate(letterDrawable, {
      draw: ['0 0', '0 1'],
      ease: 'inOutQuad',
      duration: 1000,
      delay: (_el: any, i: number) => 2000 + i * 140,
    });

    // Then fade the cover to reveal textured layers beneath
    const cutGroup = svgRef.current.querySelector(`#${uid}-cut`);
    if (cutGroup) {
      animate(cutGroup, {
        opacity: [1, 0],
        duration: 800,
        ease: 'outQuad',
        delay: 3800,
        onComplete: () => setShowWords(true),
      });
    }
  }, [uid]);

  return (
    <div className="text-center">
      <svg ref={svgRef} viewBox="0 0 300 100" className="w-full max-w-[320px] mx-auto" preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id={`${uid}-paper`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.4" numOctaves="5" result="noise" />
            <feDiffuseLighting in="noise" lightingColor={tierColor} surfaceScale="1.8" result="lit">
              <feDistantLight azimuth="45" elevation="55" />
            </feDiffuseLighting>
            <feBlend in="SourceGraphic" in2="lit" mode="screen" />
          </filter>
          <radialGradient id={`${uid}-glow`} cx="50%" cy="30%" r="60%">
            <stop offset="0%" stopColor={tierColor} stopOpacity="0.12" />
            <stop offset="100%" stopColor={tierColor} stopOpacity="0" />
          </radialGradient>
          {/* Rook mask for fill layers */}
          <mask id={`${uid}-rookMask`}>
            <g transform="translate(131, 1) scale(0.42)"><path d={ROOK_FILL_PATH} fill="white" /></g>
          </mask>
        </defs>

        {/* Background glow */}
        <rect width="300" height="100" fill={`url(#${uid}-glow)`} />

        {/* Layered rook art (revealed when cover fades) */}
        <rect width="300" height="100" mask={`url(#${uid}-rookMask)`} fill={tierColor} opacity="0.15" filter={`url(#${uid}-paper)`} />

        {/* Rule lines art layer (subtle) */}
        <line x1="20" y1="18" x2="125" y2="18" stroke={tierColor} strokeWidth="0.5" opacity="0.12" />
        <line x1="175" y1="18" x2="280" y2="18" stroke={tierColor} strokeWidth="0.5" opacity="0.12" />

        {/* SIEGE text art layer */}
        <g transform="translate(92, 50)">
          <text fontFamily="JetBrains Mono, monospace" fontSize="30" fontWeight="600" letterSpacing="8" fill="rgba(255,255,255,0.85)">SIEGE</text>
        </g>

        {/* ── Cover group — all traces on top, then fades ── */}
        <g id={`${uid}-cut`}>
          <rect width="300" height="100" fill="#0d1117" opacity="0.97" />

          {/* Rook outline trace */}
          <g id={`${uid}-rookTrace`} transform="translate(131, 1) scale(0.42)" stroke={tierColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
            {ROOK_STROKE_PATHS.map((d, i) => <path key={i} d={d} />)}
          </g>

          {/* Rule line traces */}
          <g id={`${uid}-rules`}>
            <line x1="20" y1="18" x2="125" y2="18" stroke={tierColor} strokeWidth="0.5" opacity="0.3" />
            <line x1="175" y1="18" x2="280" y2="18" stroke={tierColor} strokeWidth="0.5" opacity="0.3" />
          </g>

          {/* SIEGE letter traces */}
          <g id={`${uid}-letters`} transform="translate(96, 72)" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 0 C6 0 0 3.5 0 8 C0 12.5 4.5 15 9.5 16 C14 17 18 19.5 18 24 C18 28.5 14 32 5 32" stroke="rgba(255,255,255,0.85)" />
            <path d="M32 0 V32" stroke="rgba(255,255,255,0.85)" />
            <path d="M54 0 H40 V16 H52 M40 16 V32 H54" stroke="rgba(255,255,255,0.85)" />
            <path d="M78 4 C74 0 68 0 63 3 C59 6 58 11 58 16 C58 21 59 26 63 29 C68 32 74 32 78 28 V16 H70" stroke="rgba(255,255,255,0.85)" />
            <path d="M100 0 H86 V16 H98 M86 16 V32 H100" stroke="rgba(255,255,255,0.85)" />
          </g>
        </g>
      </svg>

      {/* Spelled-out words */}
      <div className="flex items-center justify-center gap-0 mt-0">
        {words.map((w, i) => (
          <motion.span key={i} className="text-[7px] font-mono uppercase px-1"
            style={{ color: 'rgba(255,255,255,0.15)' }}
            initial={{ opacity: 0 }} animate={showWords ? { opacity: 1 } : {}}
            transition={{ delay: i * 0.06 }}>
            {w}
          </motion.span>
        ))}
      </div>
    </div>
  );
}

// V12 — Warm parchment seal: circular medallion with paper texture, rook etched inside, warm amber glow
function TitleV12() {
  const tierColor = getPhaseTierColor(MOCK_PORTFOLIO.currentPhase);
  const svgRef = useRef<SVGSVGElement>(null);
  const [showText, setShowText] = useState(false);
  const words = ['Strategically', 'Integrated', 'Equity', 'Growth', 'Engine'];
  const uid = useRef(`v12-${Math.random().toString(36).slice(2, 8)}`).current;

  useEffect(() => {
    if (!svgRef.current) return;

    // Draw the circle border first
    const circleDrawable = svg.createDrawable(svgRef.current.querySelectorAll(`#${uid}-circleBorder`));
    animate(circleDrawable, {
      draw: ['0 0', '0 1'],
      ease: 'inOutQuad',
      duration: 1200,
    });

    // Then trace the rook inside
    const rookDrawable = svg.createDrawable(svgRef.current.querySelectorAll(`#${uid}-rookCut path`));
    animate(rookDrawable, {
      draw: ['0 0', '0 1'],
      ease: 'inOutQuad',
      duration: 2000,
      delay: (_el: any, i: number) => 800 + i * 120,
    });

    // Fade the inner cover to reveal the textured rook
    const cutGroup = svgRef.current.querySelector(`#${uid}-innerCut`);
    if (cutGroup) {
      animate(cutGroup, {
        opacity: [1, 0],
        duration: 600,
        ease: 'outQuad',
        delay: 3200,
        onComplete: () => setShowText(true),
      });
    }
  }, [uid]);

  return (
    <div className="text-center">
      <svg ref={svgRef} viewBox="0 0 130 130" className="w-[110px] h-[110px] mx-auto mb-2" preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id={`${uid}-paper`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.35" numOctaves="5" result="noise" />
            <feDiffuseLighting in="noise" lightingColor={tierColor} surfaceScale="2.2" result="lit">
              <feDistantLight azimuth="45" elevation="60" />
            </feDiffuseLighting>
            <feBlend in="SourceGraphic" in2="lit" mode="screen" />
          </filter>
          <filter id={`${uid}-blur`}><feGaussianBlur stdDeviation="4" /></filter>
          <radialGradient id={`${uid}-sealGlow`} cx="50%" cy="45%" r="50%">
            <stop offset="0%" stopColor={tierColor} stopOpacity="0.25" />
            <stop offset="70%" stopColor={tierColor} stopOpacity="0.06" />
            <stop offset="100%" stopColor={tierColor} stopOpacity="0" />
          </radialGradient>
          <radialGradient id={`${uid}-rookGrad`} cx="50%" cy="40%" r="55%">
            <stop offset="0%" stopColor={tierColor} stopOpacity="0.3" />
            <stop offset="100%" stopColor={tierColor} stopOpacity="0.08" />
          </radialGradient>
          <clipPath id={`${uid}-circleClip`}><circle cx="65" cy="65" r="48" /></clipPath>
          <mask id={`${uid}-rookMask`}>
            <g transform="translate(33, 18) scale(0.95)"><path d={ROOK_FILL_PATH} fill="white" /></g>
          </mask>
        </defs>

        {/* Background glow */}
        <circle cx="65" cy="65" r="55" fill={`url(#${uid}-sealGlow)`} />

        {/* Circle border — drawn by anime.js */}
        <circle id={`${uid}-circleBorder`} cx="65" cy="65" r="48" fill="none" stroke={tierColor} strokeWidth="1" opacity="0.3" />

        {/* Inner content clipped to circle */}
        <g clipPath={`url(#${uid}-circleClip)`}>
          {/* Layered textured rook art */}
          <rect width="130" height="130" mask={`url(#${uid}-rookMask)`} fill={`url(#${uid}-rookGrad)`} filter={`url(#${uid}-paper)`} />
          {/* Shadow */}
          <g transform="translate(34, 20) scale(0.95)">
            <path d={ROOK_FILL_PATH} fill="#000" opacity="0.1" filter={`url(#${uid}-blur)`} />
          </g>

          {/* Inner cover — hides rook art, anime.js fades this out */}
          <g id={`${uid}-innerCut`}>
            <circle cx="65" cy="65" r="48" fill="#0d1117" opacity="0.97" />
            {/* Dashed hint */}
            <g transform="translate(33, 18) scale(0.95)">
              <path d={ROOK_FILL_PATH} fill="none" stroke={tierColor} strokeWidth="0.3" strokeDasharray="3,4" opacity="0.06" />
            </g>
            {/* Drawable rook stroke — traced by anime.js */}
            <g id={`${uid}-rookCut`} transform="translate(33, 18) scale(0.95)" stroke={tierColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
              {ROOK_STROKE_PATHS.map((d, i) => <path key={i} d={d} />)}
            </g>
          </g>
        </g>
      </svg>

      {/* Rule lines + text */}
      <motion.div className="flex items-center justify-center gap-4 mb-1"
        initial={{ opacity: 0 }} animate={{ opacity: showText ? 1 : 0 }}
        transition={{ duration: 0.4 }}>
        <div className="h-px flex-1 max-w-[50px]" style={{ background: `linear-gradient(90deg, transparent, ${tierColor}15)` }} />
        <div className="h-px flex-1 max-w-[50px]" style={{ background: `linear-gradient(270deg, transparent, ${tierColor}15)` }} />
      </motion.div>

      <h1 className="text-[28px] font-mono font-semibold tracking-[0.2em] text-white/90 leading-none">
        {'SIEGE'.split('').map((c, i) => (
          <motion.span key={i} initial={{ opacity: 0, y: 8 }}
            animate={showText ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: i * 0.07 }}
            style={{ display: 'inline-block' }}>{c}</motion.span>
        ))}
      </h1>
      <div className="flex items-center justify-center gap-0 mt-1">
        {words.map((w, i) => (
          <motion.span key={i} className="text-[7px] font-mono uppercase px-1"
            style={{ color: 'rgba(255,255,255,0.15)' }}
            initial={{ opacity: 0 }} animate={showText ? { opacity: 1 } : {}}
            transition={{ delay: 0.3 + i * 0.06 }}>
            {w}
          </motion.span>
        ))}
      </div>
    </div>
  );
}

// ─── Wrapper ────────────────────────────────────────────────────────────────

function VariationCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="relative bg-white/[0.02] border border-white/[0.05] rounded-xl p-8 backdrop-blur-md">
      <span className="absolute top-3 left-4 text-[9px] font-mono tracking-wider uppercase text-white/25">
        {label}
      </span>
      <div className="mt-4">{children}</div>
    </div>
  );
}

// ─── Main Preview ───────────────────────────────────────────────────────────

export function SiegePreview() {
  const [section, setSection] = useState<'title'>('title');

  return (
    <PageWrapper warmth={0.08}>
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-[640px] mx-auto px-5 py-8">
          <h2 className="text-[10px] font-mono tracking-[0.2em] uppercase text-white/30 mb-6 text-center">
            S.I.E.G.E. Component Variations — Title
          </h2>

          <div className="flex flex-col gap-6">
            <VariationCard label="V1 — Castle Icon + Staggered">
              <TitleV1 />
            </VariationCard>

            <VariationCard label="V2 — Fortress Above + Divider">
              <TitleV2 />
            </VariationCard>

            <VariationCard label="V3 — Rooks Flanking + Ultralight">
              <TitleV3 />
            </VariationCard>

            <VariationCard label="V4 — Castle Replaces the S">
              <TitleV4 />
            </VariationCard>

            <VariationCard label="V5 — Battlement Border Trace">
              <TitleV5 />
            </VariationCard>

            <VariationCard label="V6 — Tower + Midpoint Dots">
              <TitleV6 />
            </VariationCard>

            <VariationCard label="V7 — Castle Between Rule Lines">
              <TitleV7 />
            </VariationCard>

            <VariationCard label="V8 — Fortress Card + Dots">
              <TitleV8 />
            </VariationCard>

            {/* ── Anime.js Draw Variations ── */}
            <div className="mt-6 mb-4">
              <p className="text-[9px] font-mono tracking-[0.25em] uppercase text-white/20 text-center">
                anime.js SVG path draw variations
              </p>
            </div>

            <VariationCard label="V9 — Layered Depth Reveal (inline rook)">
              <TitleV9 />
            </VariationCard>

            <VariationCard label="V10 — Outlined Rook Behind S.I.E.G.E.">
              <TitleV10 />
            </VariationCard>

            <VariationCard label="V11 — Full-Canvas Trace + Reveal">
              <TitleV11 />
            </VariationCard>

            <VariationCard label="V12 — Parchment Seal (circle trace + reveal)">
              <TitleV12 />
            </VariationCard>
          </div>
        </div>
      </main>
    </PageWrapper>
  );
}
